import React, { useEffect, useState } from "react";
import { Modal, Button, Spin, Empty, DatePicker, Alert, message } from "antd";
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const { RangePicker } = DatePicker;

/**
 * 共用：氣象溫濕度圖表（每小時一筆最高 / 最低溫）
 *
 * props
 *  - open, onClose
 *  - fetchChart: ({ range, start, end }) => Promise<{ code, data }>
 *      data.history:     [{ time, temperature, humidity, temp_max, temp_min, temp_diff }]
 *      data.window:      最高 / 最低溫的計算區間（小時），由後端決定
 *      data.latest_time: 該站最新一筆有溫度的整點
 *      data.is_stale:    最新一筆是否已過時（快捷區間會自動以它為終點）
 *  - title
 */

const RANGE_OPTIONS = ["24h", "72h", "7d", "30d"];
const X_INTERVAL = { "24h": 2, "72h": 5, "7d": 11, "30d": 47 };

const formatHour = (t) => {
  if (!t) return "";
  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:00`;
};

const UNIT = {
  temperature: "°C", temp_max: "°C", temp_min: "°C", temp_diff: "°C", humidity: "%",
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div style={{ background: "var(--content-background)", border: "1px solid var(--border-color)", padding: "8px 12px", borderRadius: 6, fontSize: 12 }}>
      <div style={{ marginBottom: 4, color: "var(--secondary-text)" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}：{p.value != null ? `${p.value}${UNIT[p.dataKey] || ""}` : "—"}
        </div>
      ))}
      {row.temp_diff != null && !payload.some((p) => p.dataKey === "temp_diff") && (
        <div style={{ color: "var(--secondary-text)", marginTop: 4, borderTop: "1px solid var(--border-color)", paddingTop: 4 }}>
          溫差：{row.temp_diff}°C
        </div>
      )}
    </div>
  );
};

export default function WeatherChartModal({ open, onClose, fetchChart, title = "氣象數據圖表" }) {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [range, setRange] = useState("72h");
  const [customRange, setCustomRange] = useState(null);

  const load = async (opts) => {
    setLoading(true);
    try {
      const res = await fetchChart(opts);
      if (res?.code === 200) setChartData(res.data);
    } catch (err) {
      setChartData(null);
      message.error(err.uiMessage || "載入圖表資料失敗");
    } finally {
      setLoading(false);
    }
  };

  const toOpts = (nextRange, nextCustom) =>
    nextCustom
      ? { start: nextCustom[0].toISOString(), end: nextCustom[1].toISOString() }
      : { range: nextRange || "72h" };

  useEffect(() => {
    if (open) load(toOpts(range, customRange));
  }, [open]);

  const handleRange = (key) => {
    setRange(key);
    setCustomRange(null);
    load(toOpts(key, null));
  };

  const handleCustom = (dates) => {
    const nextCustom = dates || null;
    const nextRange = dates ? null : range || "72h";
    setCustomRange(nextCustom);
    setRange(nextRange);
    load(toOpts(nextRange, nextCustom));
  };

  // 只畫有最高 / 最低溫的小時（後端已過濾，這裡再保險一次）
  const history = (chartData?.history || [])
    .filter((h) => h.temp_max != null && h.temp_min != null)
    .map((h) => ({ ...h, label: formatHour(h.time) }));

  const xInterval = customRange
    ? Math.max(1, Math.round((customRange[1] - customRange[0]) / 3600000 / 12))
    : X_INTERVAL[range] ?? 5;
  const windowHours = chartData?.window ?? 1;
  const windowNote =
    windowHours > 1
      ? `最高 / 最低溫：每小時一筆，取當小時與前 ${windowHours - 1} 小時的觀測值（氣象站每小時僅一筆觀測）`
      : "最高 / 最低溫：每小時一筆，取該小時內的觀測值";

  // 快捷區間且最新資料已過時 → 提示圖表終點是最新一筆
  const showStale = !customRange && chartData?.is_stale && chartData?.latest_time;

  const emptyText = !chartData?.latest_time
    ? "此氣象站目前沒有溫度資料，請改選其他測站"
    : "此區間沒有溫度資料";

  return (
    <Modal
      title={`${title}${chartData?.station_name ? ` — ${chartData.station_name}` : ""}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={880}
      bodyStyle={{ padding: "16px" }}
    >
      {/* 控制列：放在 loading 外，避免切換時整列消失 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        {RANGE_OPTIONS.map((key) => (
          <Button key={key} size="small" type={range === key ? "primary" : "default"} onClick={() => handleRange(key)}>
            {key}
          </Button>
        ))}
        <RangePicker
          size="small"
          showTime={{ format: "HH:00" }}
          format="MM/DD HH:00"
          value={customRange}
          onChange={handleCustom}
          allowClear
          placeholder={["開始時間", "結束時間"]}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px" }}>
          <Spin tip="載入資料中..." />
        </div>
      ) : history.length === 0 ? (
        <Empty description={emptyText} />
      ) : (
        <>
          {showStale && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`目前沒有新的溫度觀測，圖表顯示至最新一筆：${formatHour(chartData.latest_time)}`}
            />
          )}

          <div style={{ marginBottom: 8, fontWeight: 500, color: "var(--primary-text)" }}>溫度 (°C)</div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={history} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={xInterval} />
              <YAxis tick={{ fontSize: 11 }} unit="°C" domain={["auto", "auto"]} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconSize={12} wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="temp_max" name="最高溫" stroke="#ff4d4f" dot={false} strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="temp_min" name="最低溫" stroke="#4096ff" dot={false} strokeWidth={2} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>

          <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500, color: "var(--primary-text)" }}>溫差 (°C)</div>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={history} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={xInterval} />
              <YAxis tick={{ fontSize: 11 }} unit="°C" />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="temp_diff" name="溫差" fill="#fa8c16" />
            </BarChart>
          </ResponsiveContainer>

          <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500, color: "var(--primary-text)" }}>濕度 (%)</div>
          <ResponsiveContainer width="100%" height={170}>
            <ComposedChart data={history} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={xInterval} />
              <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
              <Tooltip content={<ChartTooltip />} />
              <Legend iconSize={12} wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="humidity" name="濕度" stroke="#1890ff" dot={false} strokeWidth={2} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>

          <div style={{ fontSize: 11, color: "var(--secondary-text)", marginTop: 8, display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span>{windowNote}</span>
            <span>氣象站：{chartData?.station_name || chartData?.station_id}</span>
          </div>
        </>
      )}
    </Modal>
  );
}